import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { profileService, pagePerformanceService } from "@/services";
import { useAuth } from "@/contexts/AuthContext";
import { Responsive, useContainerWidth } from "react-grid-layout";
import type { Layout, LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

type Layouts = { [breakpoint: string]: LayoutItem[] };

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import {
  Search,
  Timer,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  HardDrive,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type DateFilter = "7days" | "30days" | "90days" | "all";

interface PerformanceRecord {
  id: string;
  created: string;
  path: string;
  full_page_load: number | null;
  ttfb: number | null;
  dns_time: number | null;
  tcp_time: number | null;
  download_time: number | null;
  dom_processing_time: number | null;
  resource_count: number | null;
  first_contentful_paint: number | null;
  largest_contentful_paint: number | null;
  total_blocking_time: number | null;
  total_transfer_size: number | null;
  total_resource_size: number | null;
  largest_resource_size: number | null;
  largest_resource_url: string | null;
}

interface ThresholdLegendProps {
  thresholds: {
    good: string;
    warning: string;
    poor: string;
  };
}

function ThresholdLegend({ thresholds }: ThresholdLegendProps) {
  return (
    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-green-500" />
        {thresholds.good}
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        {thresholds.warning}
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-red-500" />
        {thresholds.poor}
      </span>
    </div>
  );
}

const DYNAMIC_ROUTE_PATTERNS = [
  { pattern: /^\/vendors\/[^/]+$/, normalized: "/vendors/:id" },
  { pattern: /^\/invoices\/[^/]+$/, normalized: "/invoices/:id" },
  { pattern: /^\/price-books\/[^/]+$/, normalized: "/price-books/:id" },
  { pattern: /^\/configuration\/external-systems\/[^/]+$/, normalized: "/configuration/external-systems/:id" },
  { pattern: /^\/field-tickets\/[^/]+$/, normalized: "/field-tickets/:id" },
];

function normalizePathForAggregation(path: string): string {
  for (const route of DYNAMIC_ROUTE_PATTERNS) {
    if (route.pattern.test(path)) {
      return route.normalized;
    }
  }
  return path;
}

function calculatePercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
}

function getLoadTimeColor(ms: number): string {
  if (ms < 2000) return "text-green-600";
  if (ms < 3000) return "text-amber-600";
  return "text-red-600";
}

function getTtfbColor(ms: number): string {
  if (ms < 500) return "text-green-600";
  if (ms < 1000) return "text-amber-600";
  return "text-red-600";
}

function getSlowRateColor(rate: number): string {
  if (rate < 10) return "text-green-600";
  if (rate < 25) return "text-amber-600";
  return "text-red-600";
}

function getWeightColor(bytes: number): string {
  if (bytes < 500 * 1024) return "text-green-600";
  if (bytes < 1024 * 1024) return "text-amber-600";
  return "text-red-600";
}

function getFcpColor(ms: number): string {
  if (ms < 1800) return "text-green-600";
  if (ms < 3000) return "text-amber-600";
  return "text-red-600";
}

function getLcpColor(ms: number): string {
  if (ms < 2500) return "text-green-600";
  if (ms < 4000) return "text-amber-600";
  return "text-red-600";
}

function getTbtColor(ms: number): string {
  if (ms < 200) return "text-green-600";
  if (ms < 600) return "text-amber-600";
  return "text-red-600";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const LAYOUT_STORAGE_KEY = "pagePerfAnalysisLayout";

const DEFAULT_LAYOUTS: Layouts = {
  lg: [
    { i: "filters", x: 0, y: 0, w: 12, h: 2, static: true },
    { i: "avgLoad", x: 0, y: 2, w: 3, h: 4 },
    { i: "p95Load", x: 3, y: 2, w: 3, h: 4 },
    { i: "slowPages", x: 6, y: 2, w: 3, h: 4 },
    { i: "pagesTracked", x: 9, y: 2, w: 3, h: 4 },
    { i: "avgFcp", x: 0, y: 6, w: 3, h: 4 },
    { i: "avgLcp", x: 3, y: 6, w: 3, h: 4 },
    { i: "avgTbt", x: 6, y: 6, w: 3, h: 4 },
    { i: "avgWeight", x: 9, y: 6, w: 3, h: 4 },
    { i: "trendsChart", x: 0, y: 10, w: 12, h: 8 },
    { i: "summaryTable", x: 0, y: 18, w: 12, h: 10 },
    { i: "slowDetailsTable", x: 0, y: 28, w: 6, h: 10 },
    { i: "heavyResourcesTable", x: 6, y: 28, w: 6, h: 10 },
  ],
  md: [
    { i: "filters", x: 0, y: 0, w: 10, h: 2, static: true },
    { i: "avgLoad", x: 0, y: 2, w: 5, h: 4 },
    { i: "p95Load", x: 5, y: 2, w: 5, h: 4 },
    { i: "slowPages", x: 0, y: 6, w: 5, h: 4 },
    { i: "pagesTracked", x: 5, y: 6, w: 5, h: 4 },
    { i: "avgFcp", x: 0, y: 10, w: 5, h: 4 },
    { i: "avgLcp", x: 5, y: 10, w: 5, h: 4 },
    { i: "avgTbt", x: 0, y: 14, w: 5, h: 4 },
    { i: "avgWeight", x: 5, y: 14, w: 5, h: 4 },
    { i: "trendsChart", x: 0, y: 18, w: 10, h: 8 },
    { i: "summaryTable", x: 0, y: 26, w: 10, h: 10 },
    { i: "slowDetailsTable", x: 0, y: 36, w: 10, h: 10 },
    { i: "heavyResourcesTable", x: 0, y: 46, w: 10, h: 10 },
  ],
  sm: [
    { i: "filters", x: 0, y: 0, w: 6, h: 3, static: true },
    { i: "avgLoad", x: 0, y: 3, w: 3, h: 4 },
    { i: "p95Load", x: 3, y: 3, w: 3, h: 4 },
    { i: "slowPages", x: 0, y: 7, w: 3, h: 4 },
    { i: "pagesTracked", x: 3, y: 7, w: 3, h: 4 },
    { i: "avgFcp", x: 0, y: 11, w: 3, h: 4 },
    { i: "avgLcp", x: 3, y: 11, w: 3, h: 4 },
    { i: "avgTbt", x: 0, y: 15, w: 3, h: 4 },
    { i: "avgWeight", x: 3, y: 15, w: 3, h: 4 },
    { i: "trendsChart", x: 0, y: 19, w: 6, h: 8 },
    { i: "summaryTable", x: 0, y: 27, w: 6, h: 10 },
    { i: "slowDetailsTable", x: 0, y: 37, w: 6, h: 10 },
    { i: "heavyResourcesTable", x: 0, y: 47, w: 6, h: 10 },
  ],
};

function DragHandle() {
  return (
    <div className="drag-handle absolute top-2 right-2 cursor-move p-1 rounded hover:bg-muted/50 z-10">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

export function PagePerfAnalysisTab() {
  const { user } = useAuth();
  const { width, containerRef, mounted } = useContainerWidth();
  const [pathFilter, setPathFilter] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("7days");
  const [slowOnly, setSlowOnly] = useState(false);
  const [layouts, setLayouts] = useState<Layouts>(() => {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_LAYOUTS;
      }
    }
    return DEFAULT_LAYOUTS;
  });

  const handleLayoutChange = useCallback((_currentLayout: Layout, allLayouts: Layouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(allLayouts));
  }, []);

  const resetLayout = useCallback(() => {
    setLayouts(DEFAULT_LAYOUTS);
    localStorage.removeItem(LAYOUT_STORAGE_KEY);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const dateRangeStart = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case "7days":
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case "30days":
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case "90days":
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      default:
        return null;
    }
  }, [dateFilter]);

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["page-performance", profile?.company_id, dateFilter, pathFilter, slowOnly],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await pagePerformanceService.list({
        dateRangeStart: dateRangeStart ?? undefined,
        path: pathFilter.trim() || undefined,
        slowOnly,
        slowThresholdMs: slowOnly ? 3000 : undefined,
        limit: 1000,
      });
      if (result.error) throw result.error;
      return result.data as PerformanceRecord[];
    },
    enabled: !!profile?.company_id,
  });

  const stats = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return { avgLoad: 0, p95Load: 0, slowCount: 0, uniquePaths: 0, avgWeight: 0, p95Weight: 0, medianFcp: 0, medianLcp: 0, medianTbt: 0 };
    }

    const loadTimes = rawData
      .map((r) => r.full_page_load)
      .filter((v): v is number => v !== null);

    const transferSizes = rawData
      .map((r) => r.total_transfer_size)
      .filter((v): v is number => v !== null && v > 0);

    const fcpValues = rawData
      .map((r) => r.first_contentful_paint)
      .filter((v): v is number => v !== null && v > 0);

    const lcpValues = rawData
      .map((r) => r.largest_contentful_paint)
      .filter((v): v is number => v !== null && v > 0);

    const tbtValues = rawData
      .map((r) => r.total_blocking_time)
      .filter((v): v is number => v !== null);

    const normalizedPaths = new Set(
      rawData.map((r) => normalizePathForAggregation(r.path))
    );

    return {
      avgLoad: loadTimes.length
        ? Math.round(loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length)
        : 0,
      p95Load: calculatePercentile(loadTimes, 95),
      slowCount: loadTimes.filter((t) => t > 3000).length,
      uniquePaths: normalizedPaths.size,
      avgWeight: transferSizes.length
        ? Math.round(transferSizes.reduce((a, b) => a + b, 0) / transferSizes.length)
        : 0,
      p95Weight: calculatePercentile(transferSizes, 95),
      medianFcp: calculatePercentile(fcpValues, 50),
      medianLcp: calculatePercentile(lcpValues, 50),
      medianTbt: calculatePercentile(tbtValues, 50),
    };
  }, [rawData]);

  const aggregatedData = useMemo(() => {
    if (!rawData) return [];

    const byPath = new Map<string, { loads: number[]; ttfbs: number[]; weights: number[] }>();

    rawData.forEach((record) => {
      const normalizedPath = normalizePathForAggregation(record.path);
      const existing = byPath.get(normalizedPath) || { loads: [], ttfbs: [], weights: [] };
      if (record.full_page_load !== null) {
        existing.loads.push(record.full_page_load);
      }
      if (record.ttfb !== null) {
        existing.ttfbs.push(record.ttfb);
      }
      if (record.total_transfer_size !== null && record.total_transfer_size > 0) {
        existing.weights.push(record.total_transfer_size);
      }
      byPath.set(normalizedPath, existing);
    });

    return Array.from(byPath.entries())
      .map(([path, data]) => ({
        path,
        views: data.loads.length,
        avgLoad: data.loads.length
          ? Math.round(data.loads.reduce((a, b) => a + b, 0) / data.loads.length)
          : 0,
        medianLoad: calculatePercentile(data.loads, 50),
        p95Load: calculatePercentile(data.loads, 95),
        avgTtfb: data.ttfbs.length
          ? Math.round(data.ttfbs.reduce((a, b) => a + b, 0) / data.ttfbs.length)
          : 0,
        slowRate: data.loads.length
          ? Math.round((data.loads.filter((t) => t > 3000).length / data.loads.length) * 100)
          : 0,
        avgWeight: data.weights.length
          ? Math.round(data.weights.reduce((a, b) => a + b, 0) / data.weights.length)
          : 0,
      }))
      .sort((a, b) => b.p95Load - a.p95Load);
  }, [rawData]);

  const trendData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    const buckets = new Map<string, { loads: number[]; ttfbs: number[] }>();

    rawData.forEach((record) => {
      const bucketKey =
        dateFilter === "7days"
          ? format(new Date(record.created), "MMM d HH:00")
          : format(new Date(record.created), "MMM d");

      const existing = buckets.get(bucketKey) || { loads: [], ttfbs: [] };
      if (record.full_page_load) existing.loads.push(record.full_page_load);
      if (record.ttfb) existing.ttfbs.push(record.ttfb);
      buckets.set(bucketKey, existing);
    });

    return Array.from(buckets.entries())
      .map(([time, data]) => ({
        time,
        avgLoad: data.loads.length
          ? Math.round(data.loads.reduce((a, b) => a + b, 0) / data.loads.length)
          : 0,
        avgTtfb: data.ttfbs.length
          ? Math.round(data.ttfbs.reduce((a, b) => a + b, 0) / data.ttfbs.length)
          : 0,
        pageViews: data.loads.length,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [rawData, dateFilter]);

  const slowRecords = useMemo(() => {
    if (!rawData) return [];

    return rawData
      .filter((r) => (r.full_page_load ?? 0) > 3000)
      .slice(0, 100)
      .map((r) => ({
        id: r.id,
        created: r.created,
        path: r.path,
        fullPageLoad: r.full_page_load,
        ttfb: r.ttfb,
        dnsTime: r.dns_time,
        tcpTime: r.tcp_time,
        downloadTime: r.download_time,
        domProcessing: r.dom_processing_time,
        resourceCount: r.resource_count,
        totalTransferSize: r.total_transfer_size,
      }));
  }, [rawData]);

  const heavyResources = useMemo(() => {
    if (!rawData) return [];

    return rawData
      .filter((r) => r.largest_resource_size && r.largest_resource_size > 100 * 1024)
      .sort((a, b) => (b.largest_resource_size ?? 0) - (a.largest_resource_size ?? 0))
      .slice(0, 50)
      .map((r) => ({
        id: r.id,
        created: r.created,
        path: r.path,
        largestResourceUrl: r.largest_resource_url,
        largestResourceSize: r.largest_resource_size,
        totalTransferSize: r.total_transfer_size,
      }));
  }, [rawData]);

  const clearFilters = () => {
    setPathFilter("");
    setDateFilter("7days");
    setSlowOnly(false);
  };

  const hasActiveFilters = pathFilter || dateFilter !== "7days" || slowOnly;

  return (
    <TooltipProvider>
    <div className="space-y-4">
      <div ref={containerRef}>
        {mounted && (
          <Responsive
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768 }}
            cols={{ lg: 12, md: 10, sm: 6 }}
            rowHeight={30}
            width={width}
            onLayoutChange={handleLayoutChange}
            dragConfig={{ enabled: true, handle: '.drag-handle' }}
            margin={[16, 16]}
            containerPadding={[0, 0]}
          >
        {/* Filters Panel */}
        <div key="filters">
          <Card className="h-full">
            <CardContent className="p-4 h-full flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by path..."
                  value={pathFilter}
                  onChange={(e) => setPathFilter(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select
                value={dateFilter}
                onValueChange={(value) => setDateFilter(value as DateFilter)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="30days">Last 30 days</SelectItem>
                  <SelectItem value="90days">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center space-x-2">
                <Switch
                  id="slow-only-analysis"
                  checked={slowOnly}
                  onCheckedChange={setSlowOnly}
                />
                <Label htmlFor="slow-only-analysis" className="text-sm">
                  Slow pages only
                </Label>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}

              <Button variant="outline" size="sm" onClick={resetLayout} className="ml-auto">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Layout
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Stat Cards */}
        <div key="avgLoad">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <Timer className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Average time from navigation start until the page is fully loaded and interactive</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Avg Load Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getLoadTimeColor(stats.avgLoad)}`}>
                {isLoading ? <Skeleton className="h-8 w-20" /> : `${stats.avgLoad}ms`}
              </div>
              <ThresholdLegend thresholds={{ good: "< 2s", warning: "2-3s", poor: "> 3s" }} />
            </CardContent>
          </Card>
        </div>

        <div key="p95Load">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <TrendingUp className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">95th percentile load time - 95% of page loads complete faster than this</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">P95 Load Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getLoadTimeColor(stats.p95Load)}`}>
                {isLoading ? <Skeleton className="h-8 w-20" /> : `${stats.p95Load}ms`}
              </div>
              <ThresholdLegend thresholds={{ good: "< 2s", warning: "2-3s", poor: "> 3s" }} />
            </CardContent>
          </Card>
        </div>

        <div key="slowPages">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="absolute top-2 left-2 h-4 w-4 text-amber-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Number of page loads that took longer than 3 seconds</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Slow Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {isLoading ? <Skeleton className="h-8 w-16" /> : stats.slowCount}
              </div>
              <p className="text-xs text-muted-foreground">&gt; 3000ms</p>
            </CardContent>
          </Card>
        </div>

        <div key="pagesTracked">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <BarChart3 className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Count of unique page types being monitored for performance</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Pages Tracked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? <Skeleton className="h-8 w-16" /> : stats.uniquePaths}
              </div>
            </CardContent>
          </Card>
        </div>

        <div key="avgFcp">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <Timer className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">First Contentful Paint median - 50% of page loads render first content faster than this value</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Median FCP (P50)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getFcpColor(stats.medianFcp)}`}>
                {isLoading ? <Skeleton className="h-8 w-20" /> : `${(stats.medianFcp / 1000).toFixed(2)}s`}
              </div>
              <ThresholdLegend thresholds={{ good: "< 1.8s", warning: "1.8-3s", poor: "> 3s" }} />
            </CardContent>
          </Card>
        </div>

        <div key="avgLcp">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <Timer className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Largest Contentful Paint median - 50% of page loads render the largest element faster than this value</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Median LCP (P50)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getLcpColor(stats.medianLcp)}`}>
                {isLoading ? <Skeleton className="h-8 w-20" /> : `${(stats.medianLcp / 1000).toFixed(2)}s`}
              </div>
              <ThresholdLegend thresholds={{ good: "< 2.5s", warning: "2.5-4s", poor: "> 4s" }} />
            </CardContent>
          </Card>
        </div>

        <div key="avgTbt">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <Timer className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Total Blocking Time median - 50% of page loads have less main thread blocking than this value</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Median TBT (P50)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getTbtColor(stats.medianTbt)}`}>
                {isLoading ? <Skeleton className="h-8 w-20" /> : `${stats.medianTbt}ms`}
              </div>
              <ThresholdLegend thresholds={{ good: "< 200ms", warning: "200-600ms", poor: "> 600ms" }} />
            </CardContent>
          </Card>
        </div>

        <div key="avgWeight">
          <Card className="h-full relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <HardDrive className="absolute top-2 left-2 h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="max-w-xs">Average compressed size of all resources downloaded for the page</p>
              </TooltipContent>
            </Tooltip>
            <DragHandle />
            <CardHeader className="pt-8 pb-2">
              <CardTitle className="text-lg">Avg Page Weight</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <div className={`text-2xl font-bold ${getWeightColor(stats.avgWeight)}`}>
                  {isLoading ? <Skeleton className="h-8 w-20" /> : formatBytes(stats.avgWeight)}
                </div>
                {stats.p95Weight > 0 && (
                  <span className="text-xs text-muted-foreground">P95: {formatBytes(stats.p95Weight)}</span>
                )}
              </div>
              <ThresholdLegend thresholds={{ good: "< 500KB", warning: "500KB-1MB", poor: "> 1MB" }} />
            </CardContent>
          </Card>
        </div>

        {/* Trends Chart */}
        <div key="trendsChart">
          <Card className="h-full relative overflow-hidden">
            <DragHandle />
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Performance Trends</CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-60px)]">
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="time" fontSize={12} className="fill-muted-foreground" />
                    <YAxis unit="ms" fontSize={12} className="fill-muted-foreground" />
                    <RechartsTooltip
                      formatter={(value: number) => [`${value}ms`, ""]}
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "var(--radius)",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avgLoad"
                      name="Avg Load Time"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="avgTtfb"
                      name="Avg TTFB"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No trend data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Table */}
        <div key="summaryTable">
          <Card className="h-full relative overflow-hidden flex flex-col">
            <DragHandle />
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-lg">Summary by Path</CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Path</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                    <TableHead className="text-right">Avg Load</TableHead>
                    <TableHead className="text-right">Median</TableHead>
                    <TableHead className="text-right">P95</TableHead>
                    <TableHead className="text-right">Avg TTFB</TableHead>
                    <TableHead className="text-right">Slow Rate</TableHead>
                    <TableHead className="text-right">Avg Weight</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      </TableRow>
                    ))
                  ) : aggregatedData.length > 0 ? (
                    aggregatedData.map((row) => (
                      <TableRow key={row.path}>
                        <TableCell className="font-mono text-sm">{row.path}</TableCell>
                        <TableCell className="text-right">{row.views}</TableCell>
                        <TableCell className={`text-right ${getLoadTimeColor(row.avgLoad)}`}>
                          {row.avgLoad}ms
                        </TableCell>
                        <TableCell className={`text-right ${getLoadTimeColor(row.medianLoad)}`}>
                          {row.medianLoad}ms
                        </TableCell>
                        <TableCell className={`text-right ${getLoadTimeColor(row.p95Load)}`}>
                          {row.p95Load}ms
                        </TableCell>
                        <TableCell className={`text-right ${getTtfbColor(row.avgTtfb)}`}>
                          {row.avgTtfb}ms
                        </TableCell>
                        <TableCell className={`text-right ${getSlowRateColor(row.slowRate)}`}>
                          {row.slowRate}%
                        </TableCell>
                        <TableCell className={`text-right ${getWeightColor(row.avgWeight)}`}>
                          {row.avgWeight > 0 ? formatBytes(row.avgWeight) : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                        No performance data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Slow Details Table */}
        <div key="slowDetailsTable">
          <Card className="h-full relative overflow-hidden flex flex-col">
            <DragHandle />
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-lg">Slow Page Details ({slowRecords.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead className="text-right">Load</TableHead>
                    <TableHead className="text-right">TTFB</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      </TableRow>
                    ))
                  ) : slowRecords.length > 0 ? (
                    slowRecords.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {format(new Date(row.created), "MMM d, h:mm a")}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.path}</TableCell>
                        <TableCell className={`text-right ${getLoadTimeColor(row.fullPageLoad ?? 0)}`}>
                          {row.fullPageLoad ?? "—"}ms
                        </TableCell>
                        <TableCell className={`text-right ${getTtfbColor(row.ttfb ?? 0)}`}>
                          {row.ttfb ?? "—"}ms
                        </TableCell>
                        <TableCell className={`text-right ${getWeightColor(row.totalTransferSize ?? 0)}`}>
                          {row.totalTransferSize ? formatBytes(row.totalTransferSize) : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        No slow page loads found (&gt; 3000ms)
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Heavy Resources Table */}
        <div key="heavyResourcesTable">
          <Card className="h-full relative overflow-hidden flex flex-col">
            <DragHandle />
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-lg">Heavy Resources ({heavyResources.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Page</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      </TableRow>
                    ))
                  ) : heavyResources.length > 0 ? (
                    heavyResources.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {format(new Date(row.created), "MMM d, h:mm a")}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.path}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground" title={row.largestResourceUrl ?? ""}>
                          {row.largestResourceUrl ? new URL(row.largestResourceUrl).pathname : "—"}
                        </TableCell>
                        <TableCell className={`text-right ${getWeightColor(row.largestResourceSize ?? 0)}`}>
                          {row.largestResourceSize ? formatBytes(row.largestResourceSize) : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                        No heavy resources found (&gt; 100KB)
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
          </Responsive>
        )}
      </div>

      {rawData && rawData.length >= 1000 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing the most recent 1000 records. Use filters to narrow down results.
        </p>
      )}
    </div>
    </TooltipProvider>
  );
}
